import React from 'react';

import { Minus } from 'lucide-react';

import {
  isFilterBoardURLName,
  isFilterCustomField,
  isFilterPostCategory,
  isFilterPostContent,
} from 'common/automations/filterUtils';
import stringSort from 'common/util/stringSort';
import unique from 'common/util/unique';

import { filterLabels } from '../util';
import BoardURLName from './BoardURLName';
import PostCategory from './PostCategory';
import PostContent from './PostContent';
import PostCustomField from './PostCustomField';

import type { Filter, Resource } from 'common/automations/constants';
import Button from 'common/uiV3/Button';
import HStack from 'common/uiV3/HStack';
import Input from 'common/uiV3/Input';
import { Select } from 'common/uiV3/Select';

export interface FormFilter extends Filter {
  id: string;
}

type FilterRowProps = {
  allowedFilters: Resource[];
  filter: FormFilter;
  filters: Filter[];
  onResourceChange: (id: string, resource: Resource) => void;
  onRemove: (id: string) => void;
  onFilterChange: (condition: FormFilter['condition'], value: FormFilter['value']) => void;
  required: boolean;
};

const FilterRow = ({
  allowedFilters,
  filter,
  filters,
  onResourceChange,
  onFilterChange,
  onRemove,
  required,
}: FilterRowProps) => {
  const resourceDropdownOptions = unique([...allowedFilters, filter.resource])
    .map((filter) => ({
      label: filterLabels[filter],
      value: filter,
    }))
    .sort(stringSort('label', 'asc', true));

  return (
    <HStack className="filterRow gap-2 grid grid-cols-[1fr_1fr_1fr_1fr_30px]">
      {required ? (
        <div className="filterName">
          <Input readOnly value={filterLabels[filter.resource]} />
        </div>
      ) : (
        <Select
          // Assert these types, since the select is not clearable
          onValueChange={(option) => option && onResourceChange(filter.id, option as Resource)}
          options={resourceDropdownOptions}
          placeholder="Select a condition"
          value={filter.resource}
        />
      )}
      <ConditionField
        key={filter.id}
        resource={filter.resource}
        condition={filter.condition}
        filters={filters}
        value={filter.value}
        onChange={(condition, value) => onFilterChange(condition, value)}
        required={required}
      />
      {required ? null : (
        <Button
          variant="plain"
          aria-label="Remove condition"
          iconOnly
          onClick={() => onRemove(filter.id)}>
          <Minus />
        </Button>
      )}
    </HStack>
  );
};

interface ConditionFieldProps<F extends Filter> {
  resource: F['resource'];
  condition: F['condition'];
  filters: Filter[];
  value: F['value'];
  onChange: <OcF extends Filter = F>(condition?: OcF['condition'], value?: OcF['value']) => void;
  required: boolean;
}

const ConditionField = ({ filters, required, ...props }: ConditionFieldProps<Filter>) => {
  if (isFilterBoardURLName(props)) {
    const { value, onChange } = props;
    return <BoardURLName value={value} onChange={onChange} required={required} />;
  } else if (isFilterPostCategory(props)) {
    const { value, onChange } = props;
    return <PostCategory value={value} onChange={onChange} required={required} filters={filters} />;
  } else if (isFilterPostContent(props)) {
    const { condition, value, onChange } = props;
    return (
      <PostContent condition={condition} value={value} onChange={onChange} required={required} />
    );
  } else if (isFilterCustomField(props)) {
    const { condition, value, onChange } = props;
    return (
      <PostCustomField
        condition={condition}
        value={value}
        onChange={onChange}
        required={required}
      />
    );
  } else {
    return null;
  }
};

export default FilterRow;
